import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AppEntry } from '@/apps/useApps';
import { AppLink } from '@/components/catalog/AppLink';

interface GptGroupProps {
  /** Apps GPT a las que el usuario tiene acceso (ya filtradas por permisos). */
  apps: readonly AppEntry[];
}

/**
 * Card única que agrupa todos los "agentes GPT". Al pulsarla abre un modal con
 * la lista de GPT válidos para el usuario; cada uno enlaza a su destino normal.
 */
export function GptGroup({ apps }: GptGroupProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-4 rounded-lg border bg-gradient-to-r from-appsol-50 to-white p-4 text-left shadow-sm transition hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-appsol-500"
      >
        <span
          aria-hidden
          className="flex h-12 w-12 flex-none items-center justify-center rounded-lg bg-appsol-900 text-white"
        >
          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 9h8M8 13h5m-9 7l3-3h7a3 3 0 003-3V7a3 3 0 00-3-3H6a3 3 0 00-3 3v10z" />
          </svg>
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-medium text-slate-900">{t('home.gpt.cardTitle')}</span>
          <span className="block text-sm text-slate-500">
            {t('home.gpt.count', { count: apps.length })}
          </span>
        </span>
        <span className="flex-none rounded-md bg-appsol-900 px-3 py-1.5 text-xs font-medium text-white">
          {t('home.gpt.open')}
        </span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('home.gpt.modalTitle')}
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 sm:p-8"
          onClick={() => setOpen(false)}
        >
          <div
            className="mt-4 w-full max-w-2xl rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h3 className="font-semibold text-appsol-900">{t('home.gpt.modalTitle')}</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t('home.gpt.close')}
                className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-appsol-500"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            <ul className="max-h-[60vh] divide-y divide-slate-100 overflow-y-auto p-2">
              {apps.map((app) => (
                <li key={app.slug}>
                  <AppLink
                    app={app}
                    className="flex items-center gap-3 rounded-md px-3 py-2.5 transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-appsol-500"
                  >
                    {app.icon_url ? (
                      <img src={app.icon_url} alt="" className="h-9 w-9 flex-none rounded" />
                    ) : (
                      <span aria-hidden className="h-9 w-9 flex-none rounded bg-appsol-50" />
                    )}
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-slate-900">{app.name}</span>
                      {app.description && (
                        <span className="block truncate text-sm text-slate-500">{app.description}</span>
                      )}
                    </span>
                  </AppLink>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
